import { createSlice } from '@reduxjs/toolkit';

import { communicationApi } from 'api';
import { transformFiltersToQuery } from './utils/transformFilterToQuery';

export const domainSlice = createSlice({
    name: 'domain',
    initialState: {
        pending: false,
        savePending: false,
        error: null,
        domains: [],
        count: 0,
        total: 0,
        currentPage: 1,
        totalPages: 1,
        deletePendingId: null,
    },
    reducers: {
        start: (state) => {
            state.error = null;
            state.pending = true;
        },
        stop: (state) => {
            state.pending = false;
        },
        setError: (state, action) => {
            state.error = action.payload;
            state.pending = false;
        },
        setPending: (state, action) => {
            state.pending = action.payload;
        },
        setDomains: (state, action) => {
            state.pending = false;
            state.count = action.payload.count;
            state.total = action.payload.total;
            state.domains = action.payload.items;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
        },
        setSavePending: (state, action) => {
            state.savePending = action.payload;
        },
        addDomain: (state, action) => {
            state.pending = false;
            state.domains = [action.payload, ...state.domains];
            state.total = state.total + 1;
            if (state.totalPages === 0) {
                state.totalPages = 1;
            }
        },
        editDomain: (state, action) => {
            state.pending = false;
            const index = state.domains.findIndex((s) => s.id === action.payload.id);
            if (index !== -1) {
                state.domains = [
                    ...state.domains.slice(0, index),
                    action.payload,
                    ...state.domains.slice(index + 1),
                ];
            }
        },
        deleteDomain: (state, action) => {
            state.deletePendingId = null;
            const index = state.domains.findIndex((s) => s.id === action.payload.id);
            if (index !== -1) {
                state.domains = [...state.domains.slice(0, index), ...state.domains.slice(index + 1)];
            }
            state.total = state.total - 1;
            if (state.total === 0) {
                state.totalPages = 0;
            }
        },
        startDelete: (state, action) => {
            state.deletePendingId = action.payload || null;
        },
        stopDelete: (state) => {
            state.deletePendingId = null;
        },
    },
});

export const {
    start,
    stop,
    setError,
    setDomains,
    setPending,
    setSavePending,
    addDomain,
    editDomain,
    deleteDomain,
    startDelete,
    stopDelete,
} = domainSlice.actions;

export const getDomainPending = (state) => state.domain.pending;
export const getDomains = (state) => state.domain.domains;
export const getDomainsTotal = (state) => state.domain.total;
export const getDomainsCurrentPage = (state) => state.domain.currentPage;
export const getDomainsTotalPages = (state) => state.domain.totalPages;
export const getDomainSavePending = (state) => state.domain.savePending;
export const getDomainDeletePendingId = (state) => state.domain.deletePendingId;

export const fetchDomainsAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            let query = transformFiltersToQuery(queryParams);
            dispatch(start());
            communicationApi
                .get('domains' + query)
                .then((response) => {
                    dispatch(setDomains(response.data));
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stop());
            return console.error('error', e);
        }
    };

export const addDomainAction =
    ({ domainDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(setSavePending(true));
            communicationApi
                .post('domains', domainDto)
                .then((response) => {
                    dispatch(addDomain(response.data));
                    dispatch(setSavePending(false));
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setSavePending(false));
                    onFailure?.(error?.response?.data);
                });
        } catch (e) {
            dispatch(setSavePending(false));
            return console.error(e.message);
        }
    };

export const updateDomainAction =
    ({ id, domainDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(setSavePending(true));
            communicationApi
                .put('domains/' + id, domainDto)
                .then((response) => {
                    dispatch(editDomain(response.data));
                    dispatch(setSavePending(false));
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setSavePending(false));
                    onFailure?.();
                });
        } catch (e) {
            dispatch(setSavePending(false));
            return console.error('error', e);
        }
    };

export const deleteDomainAction =
    ({ id, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(startDelete(id));
            communicationApi
                .delete('domains/' + id)
                .then((response) => {
                    dispatch(deleteDomain({ id }));
                    onSuccess?.();
                    return response;
                })
                .catch((error) => {
                    dispatch(stopDelete());
                    onFailure?.(error);
                });
        } catch (e) {
            dispatch(stopDelete());
            onFailure?.(e);
            return console.error(e.message);
        }
    };

export default domainSlice.reducer;
